import { Typography } from '@mui/material';
import Divider from '@mui/material/Divider';
import { styled } from '@mui/material/styles';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { ColorIndicator } from '@qpr/components';
import VerticalScroll from '@qpr/components/ScrollBar/VerticalScroll';
import React, { Fragment, useState } from 'react';
import { Link } from 'react-router-dom';
import { checkIfExternal } from '../../../utils/url-utils';

const PREFIX = 'MenuColumn';

const classes = {
  menuColumn: `${PREFIX}-menuColumn`,
  selected: `${PREFIX}-selected`,
  columnHeader: `${PREFIX}-columnHeader`,
  columnText: `${PREFIX}-columnText`,
  columnLink: `${PREFIX}-columnLink`,
  divider: `${PREFIX}-divider`,
  tabRoot: `${PREFIX}-tabRoot`,
  wrapper: `${PREFIX}-wrapper`,
  labelIcon: `${PREFIX}-labelIcon`,
  iconImg: `${PREFIX}-iconImg`,
  tabsRoot: `${PREFIX}-tabsRoot`,
  selectedIndicator: `${PREFIX}-selectedIndicator`,
  scrollContainer: `${PREFIX}-scrollContainer`,
  missingIcon: `${PREFIX}-missingIcon`,
  noIcon: `${PREFIX}-noIcon`
};

// TODO jss-to-styled codemod: The Fragment root was replaced by div. Change the tag if needed.
const Root = styled('div')(({ theme }) => ({
  [`& .${classes.menuColumn}`]: {
    paddingLeft: theme.spacing(6),
    display: 'flex',
    flexDirection: 'column'
  },

  [`& .${classes.selected}`]: {
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.primary.light
  },

  [`& .${classes.columnHeader}`]: {
    paddingBottom: theme.spacing(3),
    paddingTop: theme.spacing(3),
    paddingRight: theme.spacing(3)
  },

  [`& .${classes.columnText}`]: {
    paddingRight: theme.spacing(2)
  },

  [`& .${classes.columnLink}`]: {
    paddingRight: theme.spacing(2),
    fontStyle: 'italic'
  },

  [`& .${classes.divider}`]: {
    float: 'left'
  },

  [`& .${classes.tabRoot}`]: {
    textTransform: 'none',
    paddingRight: theme.spacing(3),
    maxWidth: '1000px',
    flexDirection: 'row',
    justifyContent: 'flex-end'
  },

  [`& .${classes.wrapper}`]: {
    flexDirection: 'row',
    justifyContent: 'flex-end'
  },

  [`& .${classes.labelIcon}`]: {
    minHeight: '0px'
  },

  [`& .${classes.iconImg}`]: {
    height: '32px',
    maxWidth: '32px',
    order: 2,
    marginBottom: '0px!important',
    fontSize: '32px',
    lineHeight: '32px'
  },

  [`& .${classes.tabsRoot}`]: {},
  [`& .${classes.selectedIndicator}`]: { display: 'none' },
  [`& .${classes.scrollContainer}`]: { flex: 'initial' },

  [`& .${classes.missingIcon}`]: {
    height: '32px',
    width: '32px',
    order: 2,
    marginBottom: '0px!important'
  },

  [`& .${classes.noIcon}`]: {
    height: '32px',
    order: 2
  }
}));

interface MenuColumnProps {
  data: Data[];
  level: number;
  tenant: string;
  values: Array<string | boolean>;
  handleChange: (newValue: string | boolean, level: number) => void;
  toggleMenu: () => void;
}

interface Data {
  _id: string;
  title: string;
  enabled: boolean;
  description?: string;
  group?: string | null;
  link: string;
  icon?: string | JSX.Element;
  children: Data[];
  rangecolor?: string;
}

const getIcon = (item: Data, fallBackIcon = '') => {
  const [icon, setIcon] = useState<any>(item.icon);
  const [hasError, setError] = useState<boolean>(false);

  if (hasError) {
    return <div className={classes.missingIcon} />;
  }

  // eslint-disable-next-line no-nested-ternary
  return typeof item.icon === 'string' ? (
    <img
      src={icon}
      className={classes.iconImg}
      alt={item.title}
      onError={() => {
        if (fallBackIcon && icon !== fallBackIcon) {
          setIcon(fallBackIcon);
        } else {
          setError(true);
        }
      }}
    />
  ) : item.icon ? (
    <div className={classes.iconImg}>{item.icon}</div>
  ) : (
    <div className={classes.noIcon} />
  );
};

const MenuColumn = ({
  data,
  level,
  tenant,
  toggleMenu,
  handleChange,
  values = []
}: MenuColumnProps) => {
  interface IData {
    [key: string]: any;
  }
  const temp: IData = {};

  const groupedData = data.reduce((obj, item) => {
    // If the length doesn't already exist as a key in the object, create it
    // eslint-disable-next-line no-prototype-builtins
    if (item.group && !obj.hasOwnProperty(item.group)) {
      obj[item.group as string] = [];
      // eslint-disable-next-line no-prototype-builtins
    } else if (!item.group && !obj.hasOwnProperty('nogroup')) {
      obj.nogroup = [];
    }

    // Push the number to its integer key
    // eslint-disable-next-line no-prototype-builtins
    if (item.group && obj.hasOwnProperty(item.group)) obj[item.group as string].push(item);
    else obj.nogroup.push(item);
    // Pass the object on to the next loop
    return obj;
  }, temp);

  return (
    <Root sx={{ display: 'flex' }}>
      <div className={classes.menuColumn} data-testid="sideways-megamenu-column">
        {Object.entries(groupedData).map((entry: [string, Data[]], index: number) => {
          return (
            <Fragment key={entry[0]}>
              <Typography
                key={entry[0]}
                variant="subtitle2"
                className={classes.columnHeader}
                style={{ visibility: entry[0] !== 'nogroup' ? 'visible' : 'hidden' }}
                align="right"
              >
                {entry[0] !== 'nogroup' ? entry[0] : '.'}
              </Typography>
              <VerticalScroll containerClassName={classes.scrollContainer}>
                <Tabs
                  orientation="vertical"
                  value={entry[1].some(item => item._id === values[level]) ? values[level] : false}
                  onChange={(event: React.ChangeEvent<{}>, newValue: string | boolean) => {
                    handleChange(newValue, level);
                  }}
                  aria-label="Vertical megamenu"
                  classes={{
                    root: classes.tabsRoot,
                    indicator: classes.selectedIndicator
                  }}
                >
                  {entry[1].map(item => {
                    if (item.children.length > 0)
                      return (
                        <Tab
                          label={
                            <>
                              <ColorIndicator color={`#${item.rangecolor}` || ''} />
                              <Typography variant="subtitle2" className={classes.columnText}>
                                {item.title}
                              </Typography>
                            </>
                          }
                          icon={getIcon(item)}
                          key={item._id}
                          value={item._id}
                          disabled={!item.enabled}
                          data-testid="sideways-megamenu-tab"
                          classes={{
                            selected: classes.selected,
                            root: classes.tabRoot,
                            labelIcon: classes.labelIcon
                          }}
                        />
                      );
                    return checkIfExternal(item.link) ? (
                      <Tab
                        disabled={!item.enabled}
                        key={item._id}
                        data-testid="sideways-megamenu-tab"
                        label={
                          <>
                            <ColorIndicator color={`#${item.rangecolor}` || ''} />
                            <Typography variant="subtitle2" className={classes.columnLink}>
                              {item.title}
                            </Typography>
                          </>
                        }
                        icon={getIcon(item)}
                        value={item._id}
                        href={item.link}
                        onClick={toggleMenu}
                        target="_blank"
                        classes={{
                          selected: classes.selected,
                          root: classes.tabRoot,
                          labelIcon: classes.labelIcon
                        }}
                      />
                    ) : (
                      <Tab
                        disabled={!item.enabled}
                        component={Link}
                        to={`/${tenant}${item.link}`}
                        onClick={toggleMenu}
                        key={item._id}
                        data-testid="sideways-megamenu-tab"
                        label={
                          <>
                            <ColorIndicator color={`#${item.rangecolor}` || ''} />
                            <Typography variant="subtitle2" className={classes.columnLink}>
                              {item.title}
                            </Typography>
                          </>
                        }
                        icon={getIcon(item)}
                        value={item._id}
                        classes={{
                          selected: classes.selected,
                          root: classes.tabRoot,
                          labelIcon: classes.labelIcon
                        }}
                      />
                    );
                  })}
                </Tabs>
              </VerticalScroll>
              {index + 1 < Object.entries(groupedData).length && <Divider />}
            </Fragment>
          );
        })}
      </div>
      <Divider orientation="vertical" flexItem className={classes.divider} />
      {data
        .filter(item => values[level] === item._id)
        .map(item => {
          if (item.children.length > 0)
            return (
              <MenuColumn
                key={item._id}
                data={item.children}
                level={level + 1}
                tenant={tenant}
                handleChange={handleChange}
                values={values}
                toggleMenu={toggleMenu}
              />
            );
          return null;
        })}
    </Root>
  );
};

export default MenuColumn;
